import { jsPDF } from "jspdf"
import { encode } from "uqr"

interface ReportRow {
	label: string
	value: string
}

interface ReportSection {
	title: string
	rows: ReportRow[]
	highlight?: ReportRow
}

interface ReportMap {
	title: string
	image: string
}

export interface ParcelReportV2Content {
	parcelId: string
	title: string
	subtitle: string
	reportLabel: string
	planningTitle: string
	planningSource: string
	planningPoliciesLabel: string
	noPlanningPoliciesLabel: string
	planningRows: ReportRow[]
	constraintsLabel: string
	constraints: string[]
	noConstraintsLabel: string
	checkedLabel: string
	checkedConstraints: string[]
	metrics: ReportRow[]
	sections: ReportSection[]
	maps: ReportMap[]
	mapAttribution: string
	parcelUrl: string
	qrTitle: string
	qrDescription: string
	disclaimer: string
	footerBrand: string
	footerSource: string
	continuedLabel: string
	pageLabel: (page: number, pageCount: number) => string
}

const PAGE_WIDTH = 215.9
const PAGE_HEIGHT = 279.4
const MARGIN = 16
const CONTENT_WIDTH = PAGE_WIDTH - MARGIN * 2
const CONTENT_TOP = 30
const CONTENT_BOTTOM = PAGE_HEIGHT - 20
const BODY_HEIGHT = CONTENT_BOTTOM - CONTENT_TOP
const HEADING_HEIGHT = 9
const SECTION_GAP = 9
const DETAIL_COLUMN_GAP = 10
const DETAIL_COLUMN_WIDTH = (CONTENT_WIDTH - DETAIL_COLUMN_GAP) / 2
const GREEN = [13, 99, 73] as const
const BLACK = [35, 35, 35] as const
const GRAY = [105, 109, 112] as const
const LINE = [224, 225, 227] as const
const PALE = [248, 249, 249] as const
const WARNING = [180, 83, 9] as const

function lines(document: jsPDF, value: string, width: number): string[] {
	return document.splitTextToSize(value, width) as string[]
}

function header(document: jsPDF, content: ParcelReportV2Content, logoDataUrl: string): void {
	document.addImage(logoDataUrl, "PNG", MARGIN, 7, 30, 10)
	document.setFont("helvetica", "normal")
	document.setFontSize(9)
	document.setTextColor(...GRAY)
	document.text(`${content.reportLabel.toUpperCase()} · ${content.parcelId}`, PAGE_WIDTH - MARGIN, 13.2, {
		align: "right",
	})
	document.setDrawColor(...LINE)
	document.setLineWidth(0.2)
	document.line(0, 20, PAGE_WIDTH, 20)
}

function heading(document: jsPDF, title: string, y: number, number: number, rightText?: string): number {
	const numberLabel = String(number).padStart(2, "0")
	document.setFont("helvetica", "normal")
	document.setFontSize(9)
	document.setTextColor(...GREEN)
	document.text(numberLabel, MARGIN, y - 0.4)
	const titleX = MARGIN + document.getTextWidth(numberLabel) + 2.5
	document.setFont("helvetica", "bold")
	document.setFontSize(12)
	document.setTextColor(...GREEN)
	document.text(title.toUpperCase(), titleX, y)
	if (rightText) {
		document.setFont("helvetica", "normal")
		document.setFontSize(8)
		document.setTextColor(...GRAY)
		document.text(rightText, PAGE_WIDTH - MARGIN, y, { align: "right" })
	}
	document.setDrawColor(...GREEN)
	document.setLineWidth(0.45)
	document.line(MARGIN, y + 3, PAGE_WIDTH - MARGIN, y + 3)
	return y + 9
}

function planningRowHeight(document: jsPDF, row: ReportRow): number {
	const labelWidth = 55
	document.setFontSize(10.5)
	document.setFont("helvetica", "normal")
	const labelLines = lines(document, row.label, labelWidth)
	document.setFont("helvetica", "bold")
	const valueLines = lines(document, row.value, CONTENT_WIDTH - labelWidth - 13)
	return Math.max(10, Math.max(labelLines.length, valueLines.length) * 5 + 4)
}

function planningRow(document: jsPDF, row: ReportRow, y: number): number {
	const labelWidth = 55
	document.setFontSize(10.5)
	document.setFont("helvetica", "normal")
	const labelLines = lines(document, row.label, labelWidth)
	document.setFont("helvetica", "bold")
	const valueLines = lines(document, row.value, CONTENT_WIDTH - labelWidth - 13)
	const height = planningRowHeight(document, row)
	document.setFont("helvetica", "normal")
	document.setTextColor(...GRAY)
	document.text(labelLines, MARGIN + 5, y + 6)
	document.setFont("helvetica", "bold")
	document.setTextColor(...BLACK)
	document.text(valueLines, PAGE_WIDTH - MARGIN - 5, y + 6, { align: "right" })
	document.setDrawColor(...LINE)
	document.setLineWidth(0.1)
	document.line(MARGIN + 5, y + height, PAGE_WIDTH - MARGIN - 5, y + height)
	return y + height
}

function constraintLines(document: jsPDF, content: ParcelReportV2Content): string[] {
	document.setFont("helvetica", "bold")
	document.setFontSize(11)
	return content.constraints.length
		? content.constraints.flatMap(value => lines(document, value, CONTENT_WIDTH - 12))
		: [content.noConstraintsLabel]
}

function checkedConstraintLayout(
	document: jsPDF,
	content: ParcelReportV2Content
): { labelWidth: number; lines: string[]; height: number } {
	document.setFont("helvetica", "bold")
	document.setFontSize(8)
	const labelWidth = Math.max(47, document.getTextWidth(content.checkedLabel.toUpperCase()) + 7)
	document.setFont("helvetica", "normal")
	document.setFontSize(9)
	const checkedLines = lines(document, content.checkedConstraints.join(" · "), CONTENT_WIDTH - labelWidth - 14)
	return {
		labelWidth,
		lines: checkedLines,
		height: Math.max(12, checkedLines.length * 4.5 + 5),
	}
}

function planningCardHeight(document: jsPDF, content: ParcelReportV2Content): number {
	let height = 9
	height += content.planningRows.length
		? content.planningRows.reduce((total, row) => total + planningRowHeight(document, row), 0)
		: 8
	height += 4 + 7 + 7
	height += constraintLines(document, content).length * 5 + 2
	if (content.checkedConstraints.length) height += checkedConstraintLayout(document, content).height
	return height + 5
}

function renderSummary(document: jsPDF, content: ParcelReportV2Content): number {
	document.setDrawColor(...LINE)
	document.setFillColor(255, 255, 255)
	document.roundedRect(MARGIN, 29, CONTENT_WIDTH, 28, 3, 3, "FD")
	document.setFont("helvetica", "bold")
	document.setFontSize(18)
	document.setTextColor(...BLACK)
	document.text(content.title, MARGIN + 7, 42)
	document.setFont("helvetica", "normal")
	document.setFontSize(11)
	document.setTextColor(...GRAY)
	document.text(lines(document, content.subtitle, CONTENT_WIDTH - 14), MARGIN + 7, 50)

	let y = heading(document, content.planningTitle, 68, 1, content.planningSource)
	const cardTop = y
	document.setFillColor(...PALE)
	document.setDrawColor(...LINE)
	document.roundedRect(MARGIN, cardTop, CONTENT_WIDTH, planningCardHeight(document, content), 2, 2, "FD")
	y += 5
	document.setFont("helvetica", "bold")
	document.setFontSize(8)
	document.setTextColor(...GRAY)
	document.text(content.planningPoliciesLabel.toUpperCase(), MARGIN + 5, y)
	y += 4
	if (content.planningRows.length) {
		for (const row of content.planningRows) y = planningRow(document, row, y)
	} else {
		document.setFont("helvetica", "normal")
		document.setFontSize(9)
		document.text(content.noPlanningPoliciesLabel, MARGIN + 5, y + 5)
		y += 8
	}
	y += 4
	document.setDrawColor(198, 200, 202)
	document.line(MARGIN + 5, y, PAGE_WIDTH - MARGIN - 5, y)
	y += 7
	document.setFont("helvetica", "bold")
	document.setFontSize(8)
	document.setTextColor(...GRAY)
	document.text(content.constraintsLabel.toUpperCase(), MARGIN + 5, y)
	y += 7
	document.setFontSize(11)
	if (content.constraints.length) document.setTextColor(...WARNING)
	else document.setTextColor(...GREEN)
	const renderedConstraintLines = constraintLines(document, content)
	document.text(renderedConstraintLines, MARGIN + 5, y)
	y += renderedConstraintLines.length * 5 + 2
	if (content.checkedConstraints.length) {
		const checked = checkedConstraintLayout(document, content)
		document.setFillColor(255, 255, 255)
		document.setDrawColor(...LINE)
		document.roundedRect(MARGIN + 5, y, CONTENT_WIDTH - 10, checked.height, 2, 2, "FD")
		document.setFont("helvetica", "bold")
		document.setFontSize(8)
		document.setTextColor(...GRAY)
		document.text(content.checkedLabel.toUpperCase(), MARGIN + 9, y + 6.5)
		document.setFont("helvetica", "normal")
		document.setFontSize(9)
		document.setTextColor(...BLACK)
		document.text(checked.lines, MARGIN + 5 + checked.labelWidth, y + 6.5)
		y += checked.height
	}
	y += 5 + 7
	const gap = 3
	const metricWidth = content.metrics.length
		? (CONTENT_WIDTH - gap * (content.metrics.length - 1)) / content.metrics.length
		: 0
	for (const [index, metric] of content.metrics.entries()) {
		const x = MARGIN + index * (metricWidth + gap)
		document.roundedRect(x, y, metricWidth, 21, 2, 2, "S")
		document.setFont("helvetica", "bold")
		document.setFontSize(7.5)
		document.setTextColor(...GRAY)
		document.text(lines(document, metric.label.toUpperCase(), metricWidth - 8), x + 4, y + 7)
		document.setFontSize(16)
		document.setTextColor(...BLACK)
		document.text(lines(document, metric.value, metricWidth - 8), x + 4, y + 16)
	}
	return y + (content.metrics.length ? 21 : 0) + 9
}

function detailCellLines(
	document: jsPDF,
	row: ReportRow,
	width: number
): { labelLines: string[]; valueLines: string[] } {
	const maxValueWidth = width * 0.55 - 2
	document.setFontSize(10)
	document.setFont("helvetica", "bold")
	const valueLines = lines(document, row.value, maxValueWidth)
	const valueWidth = Math.min(document.getTextWidth(row.value), maxValueWidth)
	document.setFont("helvetica", "normal")
	const labelLines = lines(document, row.label, width - valueWidth - 4)
	return { labelLines, valueLines }
}

function detailCellHeight(document: jsPDF, row: ReportRow | undefined, width: number): number {
	if (!row) return 0
	const { labelLines, valueLines } = detailCellLines(document, row, width)
	return Math.max(9.5, Math.max(labelLines.length, valueLines.length) * 5.1 + 3.5)
}

function detailCell(document: jsPDF, row: ReportRow | undefined, x: number, y: number, width: number): void {
	if (!row) return
	const { labelLines, valueLines } = detailCellLines(document, row, width)
	document.setFont("helvetica", "normal")
	document.setTextColor(...GRAY)
	document.text(labelLines, x, y + 5.3)
	document.setFont("helvetica", "bold")
	document.setTextColor(...BLACK)
	document.text(valueLines, x + width, y + 5.3, { align: "right" })
}

class PageFlow {
	y = CONTENT_TOP
	sectionNumber = 1

	constructor(readonly document: jsPDF) {}

	fits(height: number): boolean {
		return this.y + height <= CONTENT_BOTTOM
	}

	newPage(): boolean {
		if (this.y <= CONTENT_TOP) return false
		this.document.addPage()
		this.y = CONTENT_TOP
		return true
	}

	ensure(height: number): void {
		if (!this.fits(height)) this.newPage()
	}
}

interface FlowLine {
	height: number
	draw: (y: number) => void
}

function isWideRow(document: jsPDF, row: ReportRow): boolean {
	document.setFontSize(10)
	document.setFont("helvetica", "bold")
	return lines(document, row.value, DETAIL_COLUMN_WIDTH * 0.55 - 2).length > 1
}

function wideDetailLine(document: jsPDF, row: ReportRow): FlowLine {
	document.setFontSize(10)
	document.setFont("helvetica", "bold")
	const valueLines = lines(document, row.value, CONTENT_WIDTH)
	const height = valueLines.length * 4.1 + 10.5
	return {
		height,
		draw: y => {
			document.setFontSize(10)
			document.setFont("helvetica", "normal")
			document.setTextColor(...GRAY)
			document.text(row.label, MARGIN, y + 5.3)
			document.setFont("helvetica", "bold")
			document.setTextColor(...BLACK)
			document.text(valueLines, MARGIN, y + 10.8)
			document.setDrawColor(...LINE)
			document.setLineWidth(0.1)
			document.line(MARGIN, y + height, PAGE_WIDTH - MARGIN, y + height)
		},
	}
}

function detailLines(document: jsPDF, rows: ReportRow[]): FlowLine[] {
	const result: FlowLine[] = []
	const pairedRows = rows.filter(row => !isWideRow(document, row))
	const wideRows = rows.filter(row => isWideRow(document, row))
	for (let index = 0; index < pairedRows.length; index += 2) {
		const left = pairedRows[index]
		const right = pairedRows[index + 1]
		const height = Math.max(
			detailCellHeight(document, left, DETAIL_COLUMN_WIDTH),
			detailCellHeight(document, right, DETAIL_COLUMN_WIDTH)
		)
		result.push({
			height,
			draw: y => {
				const rightX = MARGIN + DETAIL_COLUMN_WIDTH + DETAIL_COLUMN_GAP
				detailCell(document, left, MARGIN, y, DETAIL_COLUMN_WIDTH)
				detailCell(document, right, rightX, y, DETAIL_COLUMN_WIDTH)
				document.setDrawColor(...LINE)
				document.setLineWidth(0.1)
				document.line(MARGIN, y + height, MARGIN + DETAIL_COLUMN_WIDTH, y + height)
				if (right) document.line(rightX, y + height, PAGE_WIDTH - MARGIN, y + height)
			},
		})
	}
	return [...result, ...wideRows.map(row => wideDetailLine(document, row))]
}

function highlightLine(document: jsPDF, highlight: ReportRow): FlowLine {
	return {
		height: 18,
		draw: y => {
			document.setFillColor(...PALE)
			document.setDrawColor(205, 207, 209)
			document.roundedRect(MARGIN, y + 4, CONTENT_WIDTH, 14, 2, 2, "FD")
			document.setFont("helvetica", "normal")
			document.setFontSize(9)
			document.setTextColor(...BLACK)
			document.text(highlight.label, MARGIN + 5, y + 13)
			document.setFont("helvetica", "bold")
			document.setFontSize(15)
			document.setTextColor(...GREEN)
			document.text(highlight.value, PAGE_WIDTH - MARGIN - 5, y + 14, { align: "right" })
		},
	}
}

function sectionLines(document: jsPDF, section: ReportSection): FlowLine[] {
	const result = detailLines(document, section.rows)
	if (section.highlight) result.push(highlightLine(document, section.highlight))
	return result
}

function renderSection(flow: PageFlow, section: ReportSection, continuedLabel: string): void {
	const { document } = flow
	const flowLines = sectionLines(document, section)
	if (flowLines.length === 0) return
	const height = flowLines.reduce((total, line) => total + line.height, HEADING_HEIGHT)
	const fitsOnFreshPage = height <= BODY_HEIGHT
	if (!flow.fits(height) && (fitsOnFreshPage || !flow.fits(HEADING_HEIGHT + flowLines[0].height))) flow.newPage()
	const sectionNumber = ++flow.sectionNumber
	flow.y = heading(document, section.title, flow.y, sectionNumber)
	for (const line of flowLines) {
		if (!flow.fits(line.height) && flow.newPage()) {
			flow.y = heading(document, `${section.title} (${continuedLabel})`, flow.y, sectionNumber)
		}
		line.draw(flow.y)
		flow.y += line.height
	}
	flow.y += SECTION_GAP
}

function qrPanelLayout(
	document: jsPDF,
	content: ParcelReportV2Content
): { titleLines: string[]; descriptionLines: string[]; height: number } {
	const textWidth = CONTENT_WIDTH - 41
	document.setFont("helvetica", "bold")
	document.setFontSize(10)
	const titleLines = lines(document, content.qrTitle, textWidth)
	document.setFont("helvetica", "normal")
	document.setFontSize(9)
	const descriptionLines = lines(document, content.qrDescription, textWidth)
	return {
		titleLines,
		descriptionLines,
		height: Math.max(30, titleLines.length * 5 + descriptionLines.length * 4.5 + 12),
	}
}

function renderQrPanel(document: jsPDF, content: ParcelReportV2Content, y: number): number {
	const qr = encode(content.parcelUrl, { ecc: "M", border: 0 })
	const qrSize = 22
	const moduleSize = qrSize / (qr.size + 8)
	const textX = MARGIN + 35
	const { titleLines, descriptionLines, height: panelHeight } = qrPanelLayout(document, content)

	document.setDrawColor(...LINE)
	document.roundedRect(MARGIN, y, CONTENT_WIDTH, panelHeight, 3, 3, "S")
	document.setFillColor(0, 0, 0)
	for (let row = 0; row < qr.size; row++) {
		for (let column = 0; column < qr.size; column++) {
			if (qr.data[row][column]) {
				document.rect(
					MARGIN + 5 + (column + 4) * moduleSize,
					y + (panelHeight - qrSize) / 2 + (row + 4) * moduleSize,
					moduleSize,
					moduleSize,
					"F"
				)
			}
		}
	}
	document.setFont("helvetica", "bold")
	document.setFontSize(10)
	document.setTextColor(...BLACK)
	document.text(titleLines, textX, y + 11)
	document.setFont("helvetica", "normal")
	document.setFontSize(9)
	document.setTextColor(...GRAY)
	document.text(descriptionLines, textX, y + 11 + titleLines.length * 5 + 1)
	document.link(MARGIN, y, CONTENT_WIDTH, panelHeight, { url: content.parcelUrl })
	return y + panelHeight + 5
}

function disclaimerLines(document: jsPDF, content: ParcelReportV2Content): string[] {
	document.setFont("helvetica", "normal")
	document.setFontSize(8)
	return lines(document, content.disclaimer, CONTENT_WIDTH)
}

function renderClosing(flow: PageFlow, content: ParcelReportV2Content): void {
	const { document } = flow
	const disclaimer = disclaimerLines(document, content)
	flow.ensure(qrPanelLayout(document, content).height + 5 + disclaimer.length * 4 + 2)
	flow.y = renderQrPanel(document, content, flow.y)
	document.setFont("helvetica", "normal")
	document.setFontSize(8)
	document.setTextColor(...GRAY)
	document.text(disclaimer, MARGIN, flow.y + 2)
	flow.y += disclaimer.length * 4 + 2 + SECTION_GAP
}

function renderMap(flow: PageFlow, map: ReportMap, attribution: string): void {
	const { document } = flow
	const image = document.getImageProperties(map.image)
	const mapHeight = Math.min((CONTENT_WIDTH * image.height) / image.width, BODY_HEIGHT - HEADING_HEIGHT - 12)
	const mapWidth = (mapHeight * image.width) / image.height
	const mapX = MARGIN + (CONTENT_WIDTH - mapWidth) / 2
	flow.ensure(HEADING_HEIGHT + mapHeight + 6)
	const y = heading(document, map.title, flow.y, ++flow.sectionNumber)
	document.addImage(map.image, "PNG", mapX, y, mapWidth, mapHeight)
	document.setDrawColor(...LINE)
	document.rect(mapX, y, mapWidth, mapHeight, "S")
	document.setFont("helvetica", "normal")
	document.setFontSize(7)
	document.setTextColor(...GRAY)
	document.text(attribution, mapX + mapWidth, y + mapHeight + 4, { align: "right" })
	flow.y = y + mapHeight + 12
}

function renderFooter(document: jsPDF, content: ParcelReportV2Content, page: number, pageCount: number): void {
	document.setDrawColor(...LINE)
	document.setLineWidth(0.2)
	document.line(0, PAGE_HEIGHT - 14, PAGE_WIDTH, PAGE_HEIGHT - 14)
	document.setFont("helvetica", "normal")
	document.setFontSize(7.5)
	document.setTextColor(...GRAY)
	document.text(`${content.footerBrand} · ${content.pageLabel(page, pageCount)}`, MARGIN, PAGE_HEIGHT - 8)
	document.text(content.footerSource, PAGE_WIDTH - MARGIN, PAGE_HEIGHT - 8, { align: "right" })
}

function decoratePages(document: jsPDF, content: ParcelReportV2Content, logoDataUrl: string): void {
	const pageCount = document.getNumberOfPages()
	for (let page = 1; page <= pageCount; page++) {
		document.setPage(page)
		header(document, content, logoDataUrl)
		renderFooter(document, content, page, pageCount)
	}
}

export function create(content: ParcelReportV2Content, logoDataUrl: string): jsPDF {
	const document = new jsPDF({ format: "letter", unit: "mm" })
	document.setProperties({ title: content.title })
	const flow = new PageFlow(document)
	flow.y = renderSummary(document, content)
	for (const section of content.sections) renderSection(flow, section, content.continuedLabel)
	renderClosing(flow, content)
	for (const map of content.maps) renderMap(flow, map, content.mapAttribution)
	decoratePages(document, content, logoDataUrl)
	return document
}
